import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchFilter } from "../../../../hooks/useSearchFilter";
import usePagination from "../../../../hooks/usePagination";
import { Menu } from "../../models/Menu";
import { useDomainFilter } from "../../../domain/hooks/useDomainFilter";
import { useMenus } from "../../hooks/useMenus";
import { useDeleteMenu } from "../../hooks/useDeleteMenu";
import { useModal } from "../../../../hooks/useModal";
import { Column, Table } from "../../../../components/reusable-components/table/Table";
import ListHeader from "../../../../components/reusable-components/table/ListHeader";
import DomainFilterSelect from "../../../domain/components/DomainFilterSelect";
import ConfirmDeleteModal from "../../../../components/reusable-components/modals/ConfirmDeleteModal";


const MenusList: FC = () => {
  const navigate = useNavigate();

  const pagination = usePagination<keyof Menu>({
    initialPage: 1,
    initialSortBy: "id",
    initialSortDir: "desc",
  });

  const { currentPage, goToPage } = pagination;

  const { search, debouncedSearch, handleSearchChange } = useSearchFilter({
    onDebounce: () => goToPage(1),
  });

  const {
    selectedDomainId,
    handleDomainChange,
    options,
    loading: domainsLoading,
    searchInput,
    handleSearchInputChange,
  } = useDomainFilter("all", () => goToPage(1));

  const {
    fetch,
    pagination: { total },
  } = useMenus({
    params: {
      ...pagination.query,
      search: debouncedSearch,
      domain_id: selectedDomainId === "all" ? undefined : selectedDomainId,
    },
  });

  const { data: menus, loading, error, refetch } = fetch;

  const {
    fetch: { fetchData: deleteMenu, loading: deleteLoading },
  } = useDeleteMenu();

  const { target, setTarget, handleClose, open } = useModal<Menu>();

  const handleDelete = async () => {
    if (!target) return;
    await deleteMenu(target.id);
    setTarget(null);
    refetch();
  };

  const columns: Column<Menu>[] = [
    { field: "id", headerName: "ID", sortable: true },
    { field: "area_id", headerName: "Area" },
    { field: "domain_id", headerName: "Domain" },
    { field: "name", headerName: "Name", sortable: true },
    { field: "type", headerName: "Type" },
  ];

  return (
    <div>
      <ListHeader
        title="Menus"
        search={{
          value: search,
          onChange: handleSearchChange,
          placeholder: "Search by menu name...",
          loading,
          disabled: loading || deleteLoading,
        }}
        onCreate={() => navigate("/menus/create")}
        createLabel="Add a new Menu"
      >
        <DomainFilterSelect
          value={selectedDomainId}
          onChange={(val) => {
            handleDomainChange(val);
            goToPage(1);
          }}
          inputValue={searchInput}
          onInputChange={handleSearchInputChange}
          options={options}
          loading={domainsLoading}
        />
      </ListHeader>

      <Table<Menu>
        columns={columns}
        data={menus}
        bodyProps={{
          onEdit: (menu) => navigate(`/menus/edit/${menu.id}`),
          onDelete: (menu) => setTarget(menu),
        }}
        loading={loading}
        error={error}
        sortBy={pagination.sortBy}
        sortDir={pagination.sortDir}
        onSortChange={pagination.handleSortChange}
        pagination={{
          page: currentPage - 1,
          rowsPerPage: 10,
          count: total,
          onPageChange: (_, newPage) => goToPage(newPage + 1),
          onRowsPerPageChange: () => {},
        }}
      />

      <ConfirmDeleteModal<Menu>
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        loading={deleteLoading}
        row={target}
        getTitle={(menu) => menu.name}
      />
    </div>
  );
};

export default MenusList;
